/**
 * Connector Registry Service
 * Manages connector registry operations and UI specs
 */

import { TeamScopedService } from '../base.service';
import { getCachedUISpec, setCachedUISpec } from '../../utils/ui-spec-cache';

export interface Connector {
  id: string;
  connectorId?: string;
  name: string;
  description: string;
  category: string;
  iconUrl?: string;
  icon?: { url?: string; content?: string };
  isAvailable: boolean;
  isActive?: boolean;
  requiresAuth: boolean;
  requiredCredentials?: any;
  tags?: string[];
}

export interface ListConnectorsResponse {
  connectors: Connector[];
}

export interface ConnectConnectorRequest {
  connectorId: string;
  name: string;
  credentials: Record<string, any>;
}

export interface ConnectConnectorResponse {
  connectionId: string;
  message: string;
}

export interface ToolUISpec {
  toolName: string;
  connectorId: string;
  displayName: string;
  description?: string;
  inputSchema?: any;
  outputSchema?: any;
  uiComponents?: any[];
}

class ConnectorRegistryService extends TeamScopedService {
  /**
   * Get all available connectors
   */
  async getAllConnectors(teamId: string): Promise<Connector[]> {
    const response = await this.getTeam<ListConnectorsResponse>(teamId, '/connectors');
    return response.connectors || [];
  }

  /**
   * Get a specific connector by ID
   */
  async getConnector(teamId: string, connectorId: string): Promise<Connector> {
    const response = await this.getTeam<{ connector: Connector }>(teamId, `/connectors/${connectorId}`);
    return response.connector;
  }

  /**
   * Connect a connector (create a new connection)
   */
  async connectConnector(
    teamId: string,
    request: ConnectConnectorRequest
  ): Promise<ConnectConnectorResponse> {
    return this.postTeam<ConnectConnectorResponse>(teamId, '/connectors/connect', request);
  }

  /**
   * Get tools for a connector
   */
  async getTools(teamId: string, connectorId: string): Promise<any[]> {
    const response = await this.getTeam<{ tools: any[] }>(teamId, `/connectors/${connectorId}/tools`);
    return response.tools || [];
  }

  /**
   * Get UI spec for a tool
   * Uses localStorage cache (1 hour TTL) to reduce API calls
   */
  async getUISpec(teamId: string, connectorId: string, toolName: string): Promise<ToolUISpec> {
    // Check cache first
    const cached = getCachedUISpec<ToolUISpec>(teamId, connectorId, toolName);
    if (cached) {
      return cached;
    }

    // Cache miss - fetch from API
    const uiSpec = await this.getTeam<ToolUISpec>(
      teamId,
      `/connectors/${connectorId}/tools/${toolName}/ui-spec`
    );

    // Store in cache
    setCachedUISpec(teamId, connectorId, toolName, uiSpec);

    return uiSpec;
  }

  /**
   * Parse tool name to extract connector ID
   * Tool name format: "connectorId_toolName" (single underscore)
   * Legacy format: "connectorId__toolName" (double underscore) - still supported
   */
  parseToolName(fullToolName: string): { connectorId: string; toolName: string } | null {
    // Try double underscore first (legacy format)
    let parts = fullToolName.split('__');
    if (parts.length === 2) {
      return {
        connectorId: parts[0],
        toolName: parts[1],
      };
    }

    // Try single underscore (current format: core_initializeTodolist)
    parts = fullToolName.split('_');
    if (parts.length >= 2) {
      // First part is connector ID, rest is tool name
      // e.g., "core_initializeTodolist" -> connector="core", tool="initializeTodolist"
      return {
        connectorId: parts[0],
        toolName: parts.slice(1).join('_'),
      };
    }

    console.error('Failed to parse tool name:', fullToolName);
    return null;
  }
}

export const connectorRegistryService = new ConnectorRegistryService();
